import React from "react";
import { compareScenarios } from "../../utils/whatif/whatIfCompare";
import {
  formatAmount,
  formatCount,
  formatDurationShort,
  formatPercent,
} from "../../utils/whatif/whatIfFormat";
import { PARTICIPANT_CURRENCY } from "../../utils/whatif/whatIfNormalize";
import {
  getWhatIfScenariosToBe,
  getWhatIfScenarioToBeById,
} from "../../services/whatIfScenarioToBeService";
import { getWhatIfAnalysis } from "../../services/whatIfService";

// One row of the metrics table: the shared as-is baseline once, then each
// scenario's own to-be figure in its own column, then the delta BETWEEN the
// two scenarios — laid out as real columns rather than wrapped inline spans,
// so the numbers line up and are scannable at a glance. `comparison.asIs` is
// null when the live baseline for this metric couldn't be loaded.
const MetricRow = ({ label, comparison, render }) => {
  const { asIs, a, b, delta, percent } = comparison;
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return (
    <div className="wif-cs-metric-row">
      <div className="wif-cs-metric-name-cell">{label}</div>
      <div className="wif-cs-metric-cell wif-cs-metric-asis">
        {asIs != null ? render(asIs) : "—"}
      </div>
      <div className="wif-cs-metric-cell">{render(a)}</div>
      <div className="wif-cs-metric-cell">{render(b)}</div>
      <div className="wif-cs-metric-cell">
        <span className={`wif-delta wif-delta--${direction}`}>
          {delta >= 0 ? "+" : "−"}
          {render(Math.abs(delta))}
          {a !== 0 && ` (${formatPercent(percent, 1)})`}
        </span>
      </div>
    </div>
  );
};

const HeadlineStat = ({ label, valueA, valueB, note }) => (
  <div className="metric">
    <div className="metric-label">{label}</div>
    <div className="metric-value wif-cs-stat-value">
      <span>{valueA}</span>
      <span className="wif-cs-stat-sep">vs</span>
      <span>{valueB}</span>
    </div>
    {note && <div className="metric-note">{note}</div>}
  </div>
);

const CompareScenarioTab = ({ processId, companyId, timeUnit, currency, rates }) => {
  const [scenarios, setScenarios] = React.useState([]);
  const [loadError, setLoadError] = React.useState("");
  const [scenarioAId, setScenarioAId] = React.useState("");
  const [scenarioBId, setScenarioBId] = React.useState("");
  const [comparing, setComparing] = React.useState(false);
  const [compareError, setCompareError] = React.useState("");
  const [compared, setCompared] = React.useState(null);
  // The process's live as-is baseline (GET /cms/whatif/{processId}) —
  // fetched once and reused for both scenarios being compared, since neither
  // scenario's own saved record carries an as-is figure for Cycle Time or
  // Staffing Cost (only Labour Cost does). Ref rather than state: it's read
  // only inside handleCompare, and doesn't need to trigger a re-render on
  // its own.
  const asIsRef = React.useRef(null);

  const money = (amountUsd) =>
    formatAmount(amountUsd, PARTICIPANT_CURRENCY, currency, rates);
  const duration = (minutes) => formatDurationShort(minutes, timeUnit);

  React.useEffect(() => {
    let cancelled = false;
    if (!processId) {
      setScenarios([]);
      return undefined;
    }
    (async () => {
      try {
        const list = await getWhatIfScenariosToBe({ processId, companyId });
        if (!cancelled) setScenarios(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Failed to load saved what-if scenarios:", error);
        if (!cancelled) {
          setScenarios([]);
          setLoadError(
            error?.message || "Could not load saved scenarios for this process.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [processId, companyId]);

  React.useEffect(() => {
    let cancelled = false;
    asIsRef.current = null;
    if (!processId) return undefined;
    (async () => {
      try {
        const payload = await getWhatIfAnalysis(processId);
        const m = payload?.metrics || {};
        if (!cancelled) {
          asIsRef.current = {
            laborCostPerCase: m.labor_cost_per_case,
            // The true process duration — NOT `original_effort_time_minutes`,
            // which is total participant-attributed minutes across every RACI
            // role (a labor-hours total), not a process duration.
            cycleTimeMinutes: m.cycle_time_minutes,
            staffingCostUsd: m.original_staffing_cost_usd,
          };
        }
      } catch (error) {
        // Non-fatal: the metrics table just falls back to "—" for whichever
        // as-is figures aren't available (see compareScenarios' own fallback
        // to currentProcessCost for labour cost).
        console.error("Failed to load as-is baseline for comparison:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [processId]);

  const canCompare =
    scenarioAId && scenarioBId && String(scenarioAId) !== String(scenarioBId);

  const handleCompare = async () => {
    if (!canCompare || comparing) return;
    setComparing(true);
    setCompareError("");
    try {
      const [a, b] = await Promise.all([
        getWhatIfScenarioToBeById(scenarioAId),
        getWhatIfScenarioToBeById(scenarioBId),
      ]);
      setCompared({ a, b, result: compareScenarios(a, b, asIsRef.current || {}) });
    } catch (error) {
      setCompareError(error?.message || "Could not load one or both scenarios.");
      setCompared(null);
    } finally {
      setComparing(false);
    }
  };

  const result = compared?.result;
  const nameA = compared?.a?.name || "Scenario A";
  const nameB = compared?.b?.name || "Scenario B";

  return (
    <>
      <p className="muted">
        Pick two saved scenarios for this process to compare their final
        to-be effort, labour cost, staffing cost and queuing results side by
        side.
      </p>

      <div className="wif-cs-controls">
        <div className="form-group">
          <label htmlFor="wif-compare-scenario-a">Scenario 1</label>
          <select
            id="wif-compare-scenario-a"
            className="form-input"
            value={scenarioAId}
            disabled={!scenarios.length}
            onChange={(e) => setScenarioAId(e.target.value)}
          >
            <option value="">Select a scenario…</option>
            {scenarios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || `Scenario #${s.id}`}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="wif-compare-scenario-b">Scenario 2</label>
          <select
            id="wif-compare-scenario-b"
            className="form-input"
            value={scenarioBId}
            disabled={!scenarios.length}
            onChange={(e) => setScenarioBId(e.target.value)}
          >
            <option value="">Select a scenario…</option>
            {scenarios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || `Scenario #${s.id}`}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ alignSelf: "end" }}>
          <button
            type="button"
            className="primary-btn"
            disabled={!canCompare || comparing}
            onClick={handleCompare}
          >
            {comparing ? "Comparing…" : "Compare"}
          </button>
        </div>
      </div>

      {loadError && (
        <ul className="whatif-warnings">
          <li>{loadError}</li>
        </ul>
      )}
      {compareError && (
        <ul className="whatif-warnings">
          <li>{compareError}</li>
        </ul>
      )}

      {!scenarios.length && !loadError && (
        <p className="muted">
          No saved scenarios yet for this process. Save a scenario from the
          Save Scenario tab first, then come back here to compare it against
          another.
        </p>
      )}

      {result && (
        <>
          <div className="section-title" style={{ marginTop: 20 }}>
            Metrics
          </div>
          <div className="wif-cs-metrics">
            <div className="wif-cs-metric-row wif-cs-metric-head">
              <div className="wif-cs-metric-name-cell">Metric</div>
              <div className="wif-cs-metric-cell">As-Is</div>
              <div className="wif-cs-metric-cell">{nameA}</div>
              <div className="wif-cs-metric-cell">{nameB}</div>
              <div className="wif-cs-metric-cell">Change</div>
            </div>
            <MetricRow
              label="Cycle Time"
              comparison={result.metrics.cycleTimeMinutes}
              render={duration}
            />
            <MetricRow
              label="Labour Cost / Case"
              comparison={result.metrics.laborCostPerCaseUsd}
              render={money}
            />
            <MetricRow
              label="Staffing Cost"
              comparison={result.metrics.staffingCostUsd}
              render={money}
            />
          </div>

          <div className="section-title" style={{ marginTop: 20 }}>
            Queuing Analysis
          </div>

          {!result.queuing ? (
            <p className="muted">
              Neither scenario has a queuing analysis recorded, so there is
              nothing to compare on this front.
            </p>
          ) : (
            <>
              {(!result.queuing.hasA || !result.queuing.hasB) && (
                <ul className="whatif-warnings">
                  <li>
                    {!result.queuing.hasA ? nameA : nameB} was saved without a
                    queuing analysis — its queuing figures below are
                    unavailable, not zero.
                  </li>
                </ul>
              )}

              <div className="metrics-grid wif-cs-headline">
                <HeadlineStat
                  label="Total Staff"
                  valueA={formatCount(result.queuing.totalStaffA)}
                  valueB={formatCount(result.queuing.totalStaffB)}
                />
                <HeadlineStat
                  label="Total Weekly Load (hrs)"
                  valueA={
                    result.queuing.totalWeeklyLoadHoursA != null
                      ? Number(result.queuing.totalWeeklyLoadHoursA).toFixed(1)
                      : "—"
                  }
                  valueB={
                    result.queuing.totalWeeklyLoadHoursB != null
                      ? Number(result.queuing.totalWeeklyLoadHoursB).toFixed(1)
                      : "—"
                  }
                />
                <HeadlineStat
                  label="Avg Queue Wait / Case"
                  valueA={
                    result.queuing.avgQueueWaitMinutesA != null
                      ? duration(result.queuing.avgQueueWaitMinutesA)
                      : "—"
                  }
                  valueB={
                    result.queuing.avgQueueWaitMinutesB != null
                      ? duration(result.queuing.avgQueueWaitMinutesB)
                      : "—"
                  }
                />
              </div>

              <div className="wif-cs-jobtable" style={{ marginTop: 12 }}>
                <div className="wif-cs-jobrow wif-cs-jobhead">
                  <div>Job</div>
                  <div>{nameA}</div>
                  <div>{nameB}</div>
                </div>
                {result.queuing.jobs.map((row) => (
                  <div key={row.jobId} className="wif-cs-jobrow">
                    <div className="wif-cs-jobname">{row.jobName}</div>
                    <div className="wif-cs-jobcell">
                      {row.a ? (
                        <>
                          {formatCount(row.a.staff)} staff ·{" "}
                          {duration(row.a.servicePerCase)}/case
                        </>
                      ) : (
                        <span className="wif-cs-missing">not staffed here</span>
                      )}
                    </div>
                    <div className="wif-cs-jobcell">
                      {row.b ? (
                        <>
                          {formatCount(row.b.staff)} staff ·{" "}
                          {duration(row.b.servicePerCase)}/case
                        </>
                      ) : (
                        <span className="wif-cs-missing">not staffed here</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default CompareScenarioTab;
